import { useTranslation } from 'react-i18next'
import React, { useMemo } from 'react'
import {
  FieldPath, FieldValues, PathValue, useFormContext, useWatch,
} from 'react-hook-form'
import { AddressFormValues, ContactPerson } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { TraderType } from 'features/declaration/models/schemas/tradersSchema'
import { hasText } from 'shared/lib/common-util'
import { isTraderWithContactPerson, isTraderWithPersonType } from 'features/declaration/models/helpers'
import { blankAddress, blankContactPerson } from 'features/declaration/models/blankItems'
import { TraderResponse } from 'features/trader/models/types'
import searchTraders from 'features/trader/api/searchTraders'
import { useDeclarationEditorContext } from 'features/declaration/form/contexts/DeclarationEditorContext'
import DotLeafPaths from 'helpers/dot-leaf-paths'
import { DEFAULT_FIELD_RULE, FieldRule } from 'features/declaration/models/fieldRules'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { ActionMeta, components, MenuListProps } from 'react-select'

// Identity for the "currently entered name" value, kept distinct from every
// option so selecting a result is never treated as a no-op (which would skip
// onChange and the auto-fill).
const CURRENT_TRADER_VALUE = '__current_trader__'

const TraderDiscriminator = {
  CONSIGNEE: 'consignee',
  CONSIGNOR: 'consignor',
  CARRIER: 'carrier',
} as const

export type TraderTarget = keyof typeof TraderDiscriminator

export type ConsignmentOption = {
  traderId: number
  name: string
  identificationNumber: string
  address: Omit<AddressFormValues, 'id'> | null
  contactPerson: Omit<ContactPerson, 'id'> | null
  target: TraderTarget
  typeOfPerson: string
}

/** react-select option: an existing trader (object value) or a free-text name (string value). */
type TraderSelectOption = { value: ConsignmentOption | string; label: string }

export function toTraderPatch(current: TraderType | undefined, option: ConsignmentOption): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    ...current,
    name: option.name,
    identificationNumber: option.identificationNumber,
    address: {
      ...(option.address ?? blankAddress),
      id: current?.address?.id ?? null,
    },
  }

  if (current && isTraderWithPersonType(current)) {
    patch.typeOfPerson = option.typeOfPerson
  }

  if (current && isTraderWithContactPerson(current)) {
    patch.contactPerson = option.contactPerson
      ? { ...option.contactPerson, id: current.contactPerson?.id ?? null }
      : blankContactPerson
  }

  return patch
}

function mapToOption(response: TraderResponse, traderTarget: TraderTarget): ConsignmentOption {
  return {
    traderId: response.id,
    name: response.name ?? '',
    identificationNumber: response.identificationNumber ?? '',
    typeOfPerson: response.typeOfPerson ?? '',
    address: response.address ? {
      addressLine: response.address.addressLine ?? '',
      street: response.address.street ?? '',
      streetNumber: response.address.streetNumber ?? '',
      country: response.address.country ?? '',
      city: response.address.city ?? '',
      postcode: response.address.postcode ?? '',
    } : null,
    contactPerson: response.contactPerson ? {
      name: response.contactPerson.name ?? '',
      email: response.contactPerson.email ?? '',
      telephone: response.contactPerson.telephone ?? '',
    } : null,
    target: traderTarget,
  }
}

const MIN_SEARCH_LENGTH = 2
const MIN_INFIX_LENGTH = 3
const MAX_SEARCH_LENGTH = 35
const SEARCH_DEBOUNCE_MS = 500

function isSearchable(inputValue: string) {
  const query = inputValue?.trim() ?? ''

  return query.length >= MIN_SEARCH_LENGTH && query.length <= MAX_SEARCH_LENGTH
}

function createDebouncedSearch() {
  let timer: ReturnType<typeof setTimeout> | undefined
  let supersede: (() => void) | undefined

  return (
    inputValue: string,
    transitOperationId: number | null | undefined,
  ) => new Promise<TraderResponse[]>((resolve, reject) => {
    if (timer) {
      clearTimeout(timer)
    }
    supersede?.()

    if (!isSearchable(inputValue)) {
      supersede = undefined
      resolve([])
      return
    }

    supersede = () => resolve([])
    timer = setTimeout(() => {
      supersede = undefined
      searchTraders(inputValue, transitOperationId).then(resolve).catch(reject)
    }, SEARCH_DEBOUNCE_MS)
  })
}

function isPrefixOnly(inputValue: string) {
  const query = inputValue?.trim() ?? ''

  return query.length >= MIN_SEARCH_LENGTH && query.length < MIN_INFIX_LENGTH
}

function MenuListWithSearchHint(props: Readonly<MenuListProps<TraderSelectOption, false>>) {
  const { t } = useTranslation()
  const { children, selectProps } = props

  return (
    <components.MenuList {...props}>
      {children}
      {isPrefixOnly(selectProps.inputValue) && (
        <div className="text-muted small px-3 py-2">{t('declaration.p5.prefixSearchHint')}</div>
      )}
    </components.MenuList>
  )
}

const selectComponents = { MenuList: MenuListWithSearchHint }

type TraderRelPath = DotLeafPaths<TraderType>

export type TraderBasePath<TForm extends FieldValues> = {
  [P in FieldPath<TForm>]:
  PathValue<TForm, P> extends TraderType ? P : never
}[FieldPath<TForm>]

export interface TraderSearchProps<TForm extends FieldValues, TPath extends TraderBasePath<TForm>> {
  scope: TPath
  maxLength?: number
  traderTarget: TraderTarget
  fieldRule?: FieldRule
}

function TraderSearch<TForm extends FieldValues, TPath extends TraderBasePath<TForm>>({
  scope,
  maxLength,
  traderTarget,
  fieldRule = DEFAULT_FIELD_RULE,
}: Readonly<TraderSearchProps<TForm, TPath>>) {
  const { t } = useTranslation()
  const {
    setValue,
    getFieldState,
    control,
  } = useFormContext<TForm>()

  const field = (rel: TraderRelPath) => `${scope}.${rel}` as FieldPath<TForm>

  const trader = useWatch<TForm>({ control, name: scope })
  const currentName: string = (trader?.name as string) ?? ''

  const isDisabled = fieldRule.requirement === 'forbidden'
  const isInvalid = getFieldState(field('name')).invalid
  const truncatedName = maxLength ? currentName.substring(0, maxLength) : currentName
  const editableInputValue = isDisabled ? '' : truncatedName

  const debouncedSearch = useMemo(createDebouncedSearch, [])

  const { declarationData } = useDeclarationEditorContext()
  const transitOperationId = declarationData.transitOperation?.id

  const loadOptions = (inputValue: string): Promise<TraderSelectOption[]> => debouncedSearch(inputValue, transitOperationId)
    .then((details) => details.map((item) => ({
      value: mapToOption(item, traderTarget),
      label: hasText(item.identificationNumber)
        ? `${item.name ?? ''} (${item.identificationNumber})`
        : item.name ?? '',
    })))
    .catch(() => [])

  // Free-text name (typed, or "create" option): only the name is stored.
  const setTraderName = (name: string) => {
    const truncated = maxLength ? name.substring(0, maxLength) : name
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue(field('name'), truncated as any, { shouldDirty: true })
  }

  // Existing trader picked from the catalog: copy its values into the form, and keep the ids the form
  // already holds. Those ids identify the consignment detail, address and contact person rows of THIS
  // declaration slot; overwriting them with the catalog's would make the server treat another row as
  // the one being edited.
  const applyTrader = (option: ConsignmentOption) => {
    const patch = toTraderPatch(trader as TraderType | undefined, option)

    setValue(scope, patch as PathValue<TForm, TPath>, { shouldDirty: true })
  }

  return (
    <AsyncCreatableSelect<TraderSelectOption, false>
      isClearable
      isDisabled={isDisabled}
      placeholder={t('declaration.p5.typeToSearch')}
      allowCreateWhileLoading={false}
      cacheOptions
      menuPlacement="auto"
      controlShouldRenderValue={isDisabled}
      className={`select menu-list--auto trader-search ${isInvalid ? 'form-control p-0 pe-3 is-invalid' : ''} ${
        isDisabled ? 'cursor--not-allowed' : ''
      }`}
      classNamePrefix="select"
      components={selectComponents}
      loadOptions={loadOptions}
      getOptionValue={(option) => (typeof option.value === 'string'
        ? option.value
        : `trader-${option.value.traderId}-${option.label}`)}
      formatCreateLabel={(inputValue) => inputValue}
      noOptionsMessage={(obj) => {
        if (obj.inputValue.trim().length < MIN_SEARCH_LENGTH) {
          return t('declaration.p5.typeToSearch')
        }

        return isPrefixOnly(obj.inputValue) ? t('declaration.p5.prefixSearchHint') : null
      }}
      loadingMessage={() => t('common.loading')}
      inputValue={editableInputValue}
      value={hasText(currentName) ? { value: CURRENT_TRADER_VALUE, label: currentName } : null}
      onInputChange={(newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          setTraderName(newValue)
        }
      }}
      onChange={(option: TraderSelectOption | null, actionMeta: ActionMeta<TraderSelectOption>) => {
        if (!option) {
          setTraderName('')
          return
        }
        if (actionMeta.action === 'select-option' && typeof option.value === 'object') {
          applyTrader(option.value)
          return
        }
        setTraderName(typeof option.value === 'string' ? option.value : option.label)
      }}
    />
  )
}

TraderSearch.defaultProps = {
  maxLength: undefined,
}

export default TraderSearch
